import type {
  AnySimulationObjective,
  AutoCandidate,
  ObjectiveContext,
  ObjectiveEval,
} from "@opus-dt/shared-types";
import {
  getObjectiveStrategy,
  getSequenceObjectiveStrategy,
  isSequenceObjective,
} from "@opus-dt/shared-types";

// Exibição da métrica do objetivo por candidato. Delega ao REGISTRY de objetivos
// (shared-types) — a MESMA fonte que o worker usa para ranquear — evitando lógica duplicada e
// divergente. O horizonte vem do lote (nunca hardcoded), casando com o ranqueamento do backend.
// Este módulo serve o lote PARAMS. Um objetivo de SEQUÊNCIA chega aqui só se um lote
// de fila for renderizado por estas telas: o rótulo vem do registry certo (nunca um código cru),
// mas a MÉTRICA é null — ela mora em `results.perOrder`, que `AutoCandidate` não carrega.
// Mostrar "—" é o honesto; a superfície própria da fila é o drawer de sequência.

export interface ObjMetric {
  value: number | null;
  unit: string;
  label: string;
}

// A métrica de um objetivo pode depender de QUALQUER campo do lote (o prazo de
// OP_ON_TIME, por exemplo). Passar o lote inteiro, em vez de campos soltos, é o que impede que o
// próximo objetivo estreie exibindo "—" porque alguém esqueceu de encaminhar mais um argumento.
export interface BatchObjectiveCtx {
  objective: AnySimulationObjective;
  targetQuantity: number | null;
  horizonHours: number;
  /** Prazo da OP em segundos relativos à âncora da execução; null fora de OP_ON_TIME. */
  dueAtS?: number | null;
}

function ctxOf(b: BatchObjectiveCtx): ObjectiveContext {
  return { targetQuantity: b.targetQuantity, dueAtS: b.dueAtS ?? null };
}

// Exibição não precisa do esforço (só o ranqueamento do worker usa) — 0 é neutro aqui.
function evalOf(c: AutoCandidate, horizonHours: number): ObjectiveEval {
  return {
    oee: c.oee ?? NaN,
    estimatedProduction: c.estimatedProduction ?? NaN,
    horizonHours,
    effort: 0,
    // O instante MEDIDO pelo motor tem precedência sobre a extrapolação linear; ausente
    // em candidatos antigos, e o registry cai no fallback (rotulado como estimativa).
    timeToTargetS: c.timeToTargetS,
    targetReached: c.targetReached ?? undefined,
  };
}

export function objLabel(objective: AnySimulationObjective): string {
  return isSequenceObjective(objective)
    ? getSequenceObjectiveStrategy(objective).label
    : getObjectiveStrategy(objective).label;
}

export function objMetric(c: AutoCandidate, batch: BatchObjectiveCtx): ObjMetric {
  if (isSequenceObjective(batch.objective)) {
    const s = getSequenceObjectiveStrategy(batch.objective);
    return { value: null, unit: "min", label: s.label };
  }
  return getObjectiveStrategy(batch.objective).metric(evalOf(c, batch.horizonHours), ctxOf(batch));
}

/** O candidato atinge a meta do objetivo? null quando o objetivo não usa meta. */
export function meetsObjectiveTarget(c: AutoCandidate, batch: BatchObjectiveCtx): boolean | null {
  if (isSequenceObjective(batch.objective)) return null; // sequência não tem meta de quantidade
  return getObjectiveStrategy(batch.objective).meetsTarget(
    evalOf(c, batch.horizonHours),
    ctxOf(batch)
  );
}

// Δ do candidato vs baseline no termo do objetivo. `positive` = melhora — a DIREÇÃO vem do
// registry (`higherIsBetter`), nunca da unidade: "h" serve tanto ao tempo (menor é melhor) quanto
// à folga sobre o prazo (maior é melhor), e inferir pela unidade invertia o sinal de OP_ON_TIME.
export function objDelta(
  candidate: AutoCandidate,
  baseline: AutoCandidate | null,
  batch: BatchObjectiveCtx
): { text: string; positive: boolean } | null {
  if (!baseline || isSequenceObjective(batch.objective)) return null;
  const strategy = getObjectiveStrategy(batch.objective);
  const m = strategy.metric(evalOf(candidate, batch.horizonHours), ctxOf(batch));
  const b = strategy.metric(evalOf(baseline, batch.horizonHours), ctxOf(batch)).value;
  if (m.value == null || b == null) return null;
  const d = m.value - b;
  const positive = strategy.higherIsBetter ? d >= 0 : d <= 0;
  const sign = d >= 0 ? "+" : "−";
  if (m.unit === "h") return { text: `${sign}${fmtDuration(Math.abs(d))}`, positive };
  if (m.unit === "un") return { text: `${sign}${Math.abs(Math.round(d))} un`, positive };
  return { text: `${sign}${Math.abs(d).toFixed(1)} pp`, positive };
}

// Hora DECIMAL não é como a fábrica fala do relógio. "1.5 h" e "−0.1 h" eram o mesmo
// texto para candidatos separados por dez minutos: o arredondamento apagava a ordem que o ranking
// existe para mostrar. O registry passou a devolver precisão cheia; o corte de casas mora aqui,
// na borda, e a escala do texto acompanha a grandeza (h+min acima de 1 h, min, s no miúdo).
function fmtDuration(hours: number): string {
  const totalS = Math.round(hours * 3600);
  if (totalS >= 3600) {
    const h = Math.floor(totalS / 3600);
    return `${h}h${String(Math.round((totalS % 3600) / 60)).padStart(2, "0")}`;
  }
  if (totalS >= 60) return `${Math.round(totalS / 60)} min`;
  return `${totalS} s`;
}

// Δ CRU (sem formatação) no termo do objetivo: é a matéria-prima do eixo do ranking.
// Mora ao lado de `objDelta` para que o número desenhado e o número escrito venham da mesma conta.
export function objDeltaValue(
  candidate: AutoCandidate,
  baseline: AutoCandidate | null,
  batch: BatchObjectiveCtx
): number | null {
  if (!baseline) return null;
  const m = objMetric(candidate, batch).value;
  const b = objMetric(baseline, batch).value;
  return m == null || b == null ? null : m - b;
}

/** Δ de OEE vs base, em pontos percentuais. Null quando qualquer das duas pontas falta. */
export function oeeDeltaValue(candidate: AutoCandidate, baseline: AutoCandidate | null) {
  return candidate.oee == null || baseline?.oee == null ? null : candidate.oee - baseline.oee;
}

/** Pontos percentuais com sinal explícito — o "+" importa tanto quanto o "−" numa coluna de Δ. */
export function fmtPp(delta: number | null): string {
  if (delta == null) return "—";
  return `${delta >= 0 ? "+" : "−"}${Math.abs(delta).toFixed(1)} pp`;
}

export function fmtMetric(m: ObjMetric): string {
  if (m.value == null) return "—";
  if (m.unit === "%") return `${m.value.toFixed(1)}%`;
  // Folga NEGATIVA (OP atrasada) é um resultado legítimo — o sinal vem antes da duração.
  if (m.unit === "h") return `${m.value < 0 ? "−" : ""}${fmtDuration(Math.abs(m.value))}`;
  return `${Math.round(m.value).toLocaleString("pt-BR")} un`;
}
